import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Edit3, Link as LinkIcon, Loader2, Save, Clock, X, Trash2 } from "lucide-react";
import { adminAuthHeader } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINK_KINDS = [
  { value: "", label: "—" },
  { value: "imposta-password", label: "Imposta password" },
  { value: "lezione-1", label: "Lezione 1" },
  { value: "sblocco-bundle", label: "Sblocco Bundle L.2-3-4" },
];

function fmtDelay(s) {
  if (s == null) return "—";
  if (s === 0) return "subito";
  if (s < 60) return `+${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `+${m}m${r}s` : `+${m}min`;
}

/**
 * Single-template editable card. Inline editing for copy + delay + link kind.
 * Supports hard-delete via the trash icon (admin-created tags) or sync to
 * Python constant fallback (legacy tags).
 */
export default function TemplateCard({ t, onSaved, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    copy: t.copy || "",
    delay_seconds: t.delay_seconds ?? 0,
    link: t.link || "",
    enabled: t.enabled !== false,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const startEdit = () => {
    setDraft({
      copy: t.copy || "",
      delay_seconds: t.delay_seconds ?? 0,
      link: t.link || "",
      enabled: t.enabled !== false,
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/message-templates/${t.tag}`, {
        copy: draft.copy,
        delay_seconds: Number(draft.delay_seconds) || 0,
        link: draft.link || null,
        enabled: !!draft.enabled,
      }, { headers: adminAuthHeader() });
      toast.success(`Salvato ${t.tag}`);
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const hardDelete = async () => {
    if (!window.confirm(`Eliminare definitivamente il sotto-messaggio "${t.tag}"?`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/message-templates/${t.tag}`, { headers: adminAuthHeader() });
      toast.success(`Eliminato ${t.tag}`);
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <article
      className="rounded-xl p-4 border bg-white"
      style={{ borderColor: t.enabled === false ? "#fca5a5" : "#dbe3ec", opacity: t.enabled === false ? 0.65 : 1 }}
      data-testid={`template-card-${t.tag}`}
    >
      <header className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-[11px] font-mono px-2 py-0.5 rounded" style={{ background: "#f1f5f9", color: "#475569" }}>
              {t.tag}
            </code>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#fef3c7", color: "#92400e" }}>
              <Clock size={9} /> {fmtDelay(t.delay_seconds)}
            </span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#f1f5f9", color: "#475569" }}>
              msg {(t.sequence_index ?? 0) + 1}
            </span>
            {t.link && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#eff6ff", color: "#1e40af" }}>
                <LinkIcon size={9} /> {t.link}
              </span>
            )}
            {t.enabled === false && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#fee2e2", color: "#991b1b" }}>
                disabilitato
              </span>
            )}
          </div>
        </div>
        {!editing && (
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={startEdit}
              data-testid={`template-edit-btn-${t.tag}`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
              style={{ background: "#fff", borderColor: "var(--np-secondary)", color: "var(--np-secondary)" }}
            >
              <Edit3 size={11} /> Modifica
            </button>
            <button
              type="button"
              onClick={hardDelete}
              disabled={deleting}
              data-testid={`template-delete-btn-${t.tag}`}
              title="Elimina sotto-messaggio"
              className="inline-flex items-center justify-center w-7 h-7 rounded-md border"
              style={{ background: "#fff", borderColor: "#fca5a5", color: "#dc2626" }}
            >
              {deleting ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
            </button>
          </div>
        )}
      </header>

      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--np-muted)" }}>
                Delay (sec dal trigger)
              </span>
              <input
                type="number" min="0" step="1"
                value={draft.delay_seconds}
                onChange={(e) => setDraft({ ...draft, delay_seconds: e.target.value })}
                data-testid={`template-delay-${t.tag}`}
                className="mt-1 w-full px-2 py-1.5 rounded-md border text-sm font-mono"
                style={{ borderColor: "#cbd5e1" }}
              />
              <span className="block text-[10px] mt-0.5" style={{ color: "var(--np-muted)" }}>
                Verrà inviato: {fmtDelay(Number(draft.delay_seconds) || 0)}
              </span>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--np-muted)" }}>
                Tipo di link
              </span>
              <select
                value={draft.link || ""}
                onChange={(e) => setDraft({ ...draft, link: e.target.value })}
                data-testid={`template-link-${t.tag}`}
                className="mt-1 w-full px-2 py-1.5 rounded-md border text-sm"
                style={{ borderColor: "#cbd5e1" }}
              >
                {LINK_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>
          </div>
          <textarea
            value={draft.copy}
            onChange={(e) => setDraft({ ...draft, copy: e.target.value })}
            data-testid={`template-textarea-${t.tag}`}
            rows={Math.max(5, (draft.copy || "").split("\n").length + 1)}
            className="w-full p-3 rounded-lg border font-mono text-[13px] leading-relaxed"
            style={{ borderColor: "#cbd5e1", background: "#f8fafc", color: "var(--np-primary)" }}
          />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              data-testid={`template-enabled-${t.tag}`}
            />
            <span className="text-[12px]" style={{ color: "var(--np-primary)" }}>
              Sotto-messaggio attivo (uncheck = skippato dal motore)
            </span>
          </label>
          <p className="text-[10px]" style={{ color: "var(--np-muted)" }}>
            Placeholder: <code>{"{name}"}</code> · <code>{"{link}"}</code>
          </p>
          <div className="flex gap-2 justify-end">
            <button
              type="button" onClick={() => setEditing(false)} disabled={saving}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
              style={{ borderColor: "#cbd5e1", color: "#475569", background: "#fff" }}
            >
              <X size={11} /> Annulla
            </button>
            <button
              type="button" onClick={save} disabled={saving}
              data-testid={`template-save-btn-${t.tag}`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md text-white disabled:opacity-50"
              style={{ background: "var(--np-secondary)" }}
            >
              {saving ? <><Loader2 size={11} className="animate-spin" /> Salvo…</> : <><Save size={11} /> Salva</>}
            </button>
          </div>
        </div>
      ) : (
        <pre
          className="p-3 rounded-lg whitespace-pre-wrap text-[13px] leading-relaxed"
          style={{ background: "#f8fafc", color: "var(--np-primary)", fontFamily: "ui-sans-serif, system-ui" }}
        >{t.copy}</pre>
      )}
    </article>
  );
}
